// GraphQL
import {
	CoreInvestmentTestCategoryChoices,
	CoreInvestmentTestExperienceChoices,
	useUserContextQuery,
} from "@/api/graphql"

// Navigation
import { Routes } from "@/constants/routes"
import { useNavigate } from "@/lib/router"

// Context
import { useMangopayConsentStatus } from "@/context/user"

// Collects every onboarding gate an investor must clear before a pending
// investment can be paid: the experience self-assessment, (for novices) the
// knowledge test and loss-capacity simulation, and the Mangopay SCA consents.
// Returns a count of what's left, the next step's URL, and a navigate helper
// so the "pending investment" banner can hop the user through them in order.
export function useRequiredStepsBeforePendingInvestment() {
	const { data, refetch } = useUserContextQuery()

	// Navigate
	const navigate = useNavigate()

	// Mangopay SCA consents are a required step in the investor onboarding
	// flow. Only count it as actionable when the user actually has a Mangopay
	// user and the user context has loaded: during loading `allGranted` is
	// false (badges would flash on every page load for fully-onboarded users),
	// and without a Mangopay user it's false permanently (the user can't
	// clear the step — it needs backend intervention).
	const {
		isLoading: isMangopayStatusLoading,
		hasMangopayUser,
		allGranted: allConsentsGranted,
	} = useMangopayConsentStatus()

	// Hide badges when the test is already done
	const investorProfileExperience = data?.me?.investment_tests?.filter(
		(result) =>
			result.category === CoreInvestmentTestCategoryChoices.Experience,
	)[0]
	let investorTestQuestions = data?.me?.investment_tests?.filter(
		(result) =>
			result.category === CoreInvestmentTestCategoryChoices.Questions,
	)[0]
	let investorTestRisk = data?.me?.investment_tests?.filter(
		(result) =>
			result.category ===
			CoreInvestmentTestCategoryChoices.LossCalculator,
	)[0]
	/* const investorIdentity = (data?.me?.kyc?.state ===
		RiskKycStateChoices.VerificationSuccess) as Boolean */

	// How many required actions are there left?
	let amountOfRequiredActionsLeft = 0
	let requiredActions: Record<string, boolean> = {
		EXPERIENCE: !Boolean(investorProfileExperience),
		QUESTIONS:
			investorProfileExperience?.experience ===
			CoreInvestmentTestExperienceChoices.Novice
				? !Boolean(investorTestQuestions)
				: false,
		LOSS_CALCULATOR:
			investorProfileExperience?.experience ===
			CoreInvestmentTestExperienceChoices.Novice
				? !Boolean(investorTestRisk)
				: false,
		PAYMENTS:
			!isMangopayStatusLoading && hasMangopayUser && !allConsentsGranted,
	}

	// If any of these require action, set badge
	Object.keys(requiredActions).forEach((key) => {
		if (requiredActions[key] === true) {
			amountOfRequiredActionsLeft++
		}
	})

	// Determine next step URL
	const nextStep = Object.keys(requiredActions).find(
		(key) => requiredActions[key] === true,
	)
	let nextStepUrl: string | undefined = undefined
	if (amountOfRequiredActionsLeft > 0) {
		switch (nextStep) {
			case CoreInvestmentTestCategoryChoices.Experience:
				nextStepUrl = Routes.InvestorProfile
				break
			case CoreInvestmentTestCategoryChoices.Questions:
				nextStepUrl = Routes.InvestorTest
				break
			case CoreInvestmentTestCategoryChoices.LossCalculator:
				nextStepUrl = Routes.InvestorRisk
				break
			case "PAYMENTS":
				nextStepUrl = Routes.InvestorPayments
				break
		}
	}

	/**
	 * Check which step is next and then redirect to that step
	 *
	 * TODO: Add KYC and IBAN
	 */
	function onRedirectToNextStep() {
		if (nextStepUrl) {
			void navigate(nextStepUrl)
		}
	}

	return {
		refetch,
		investorProfileExperience,
		investorTestQuestions,
		investorTestRisk,
		amountOfRequiredActionsLeft,
		onRedirectToNextStep,
		nextStepUrl,
	}
}
